import { useState, useCallback } from 'react';
import i18next from 'i18next';

/**
 * Trigger Web Vibration API haptic feedback (mobile only).
 * Silently no-ops on unsupported platforms.
 * Patterns chosen for low aggression — short taps, not buzzes.
 */
function triggerHaptic(kind) {
  if (typeof navigator === 'undefined' || !navigator.vibrate) return;
  const patterns = {
    success: [10],
    warning: [12, 40, 12],
    error: [24, 50, 24],
    selection: [6],
    impact: [14],
  };
  const pattern = patterns[kind] || patterns.selection;
  try {
    navigator.vibrate(pattern);
  } catch {
    /* some browsers throw on user-gesture restrictions; ignore */
  }
}

/**
 * Enterprise Toast Hook
 *
 * Phase 1.7c additions:
 *   - `action(...)`            richer API with explicit options
 *   - `triggerHaptic(...)`     exported helper, callable outside toasts
 *   - All convenience methods accept `{ haptic: 'success' | ... }`
 *   - Undo stack collapsing: only the latest withUndo toast is visible
 */
export const useToast = () => {
  const [toasts, setToasts] = useState([]);

  /**
   * Add a toast notification
   * @param {string} message - The message to display
   * @param {string} type - 'success' | 'error' | 'warning' | 'info'
   * @param {number} duration - Auto-dismiss ms (0 = no auto-dismiss)
   * @param {Object} action - Optional { label, onClick }
   */
  const addToast = useCallback((message, type = 'success', duration = 3000, action = null) => {
    const id = Date.now() + Math.random();
    const newToast = { id, message, type, duration, action };
    setToasts((prev) => [...prev, newToast]);
    return id;
  }, []);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const success = useCallback((message, options = {}) => {
    if (options.haptic !== false) triggerHaptic(options.haptic || 'success');
    return addToast(message, 'success', options.duration ?? 3000, options.action);
  }, [addToast]);

  const error = useCallback((message, options = {}) => {
    if (options.haptic !== false) triggerHaptic(options.haptic || 'error');
    return addToast(message, 'error', options.duration ?? 5000, options.action);
  }, [addToast]);

  const warning = useCallback((message, options = {}) => {
    if (options.haptic !== false) triggerHaptic(options.haptic || 'warning');
    return addToast(message, 'warning', options.duration ?? 4000, options.action);
  }, [addToast]);

  const info = useCallback((message, options = {}) => {
    if (options.haptic) triggerHaptic(options.haptic);
    return addToast(message, 'info', options.duration ?? 3000, options.action);
  }, [addToast]);

  /**
   * Generic action toast with explicit options. Replaces any previous
   * action-toast (undo stack collapsing) so multiple rapid actions don't
   * pile up.
   *
   * @example
   *   toast.action({
   *     title: 'Marked 18 present',
   *     action: { label: 'Undo', onClick: rollback },
   *     duration: 10_000,
   *     haptic: 'success',
   *   });
   */
  const action = useCallback(({ title, type = 'success', duration = 8000, action: actionBtn, haptic = 'success' }) => {
    if (haptic) triggerHaptic(haptic);
    const id = Date.now() + Math.random();
    const newToast = { id, message: title, type, duration, action: actionBtn, isAction: true };
    setToasts((prev) => [
      // collapse: drop any other action-toasts before adding this one
      ...prev.filter(t => !t.isAction),
      newToast,
    ]);
    return id;
  }, []);

  /**
   * Show a toast with an Undo action (sugar for `action()`).
   * @param {string} message
   * @param {Function} onUndo  callback when the user taps Undo
   * @param {Object} options   { duration?, undoLabel?, type?, haptic? }
   */
  const withUndo = useCallback((message, onUndo, options = {}) => {
    return action({
      title: message,
      type: options.type || 'success',
      duration: options.duration ?? 8000,
      action: { label: options.undoLabel || i18next.t('common.undo') || 'Undo', onClick: onUndo },
      haptic: options.haptic ?? 'success',
    });
  }, [action]);

  return {
    toasts,
    addToast,
    removeToast,
    success,
    error,
    warning,
    info,
    action,
    withUndo,
    triggerHaptic,
  };
};

export { triggerHaptic };
